/**
 * "Calm Fintech" design tokens — the ONE source (ADR-0003 workstream 7: "a
 * design-token file, one source, kills the navy/teal drift").
 *
 * Values below are copied VERBATIM from the ratified token table in
 * `ValuBrief_Design_and_Roadmap.md` §1 ("Visual direction — Calm Fintech").
 * A prior audit (UI-04 in the remediation backlog) found navy re-declared as
 * four different hex values and "verified" green split across teal/green
 * across different legacy pages, precisely because there was no single
 * source — this file is that source. `/next` components must import from
 * here, never inline a hex value.
 *
 * ASSUMPTION FLAGGED: the roadmap doc defines four semantic chip colors
 * (verified / caution / amber / neutral) but the Recommendation contract's
 * `Tier` type has FIVE values (verified, indicative, gap, assumption,
 * entered). The mapping below is this session's best-fit reading, not a
 * re-litigated design decision:
 *   - verified   -> --verified   (doc: "Sourced / below-subject / good for case")
 *   - indicative -> --amber      (doc: "Sample chips" — a plausible-but-unconfirmed read)
 *   - assumption -> --caution    (doc: "Disclosed-not-relied, cautions" — an assumption
 *                                  is exactly the kind of thing that should read as a caution)
 *   - entered    -> --neutral    (doc: "Approximation / user-input chips")
 *   - gap        -> --text-secondary on --bg-app (no explicit chip color in the doc;
 *                                  a gap is an ABSENCE, not a warning, so it is
 *                                  deliberately muted rather than colored — matches
 *                                  "unknown is never zero," rendered calmly, not as
 *                                  an alarm)
 * Flagging this explicitly rather than presenting it as settled — the UI/Design-System
 * owner should confirm or correct the indicative/gap mapping specifically.
 */

/**
 * PALETTE MERGE (owner-ratified, board 06 §5 "This kills the split, per owner"):
 * values below are the ORIGINAL SITE'S exact `:root` tokens (src/server/web/index.html)
 * — the single canonical palette. Mapping (site var -> token):
 *   --navy #16314A -> accent        --navy-ink #0F2438 -> accentHover
 *   --ink #1E293B  -> textPrimary   --slate #5B6B7F    -> textSecondary
 *   --hair #E7ECF2 -> border        --mist #F6F8FB     -> bgApp
 *   --green #1E7A3D -> verified     --amber #92660A    -> amber
 *   --amberbg #FEF7E6 -> amberBg
 * Tint backgrounds with NO site counterpart (accentSoft, verifiedBg, cautionBg,
 * neutralChip, caution fg) are kept from the prior set rather than inventing new
 * hexes — flagged for the Design-System owner, not silently re-derived.
 */
export const colors = {
  bgApp: "#F6F8FB",        // site --mist
  bgSurface: "#FFFFFF",    // site --white
  border: "#E7ECF2",       // site --hair
  textPrimary: "#1E293B",  // site --ink
  textSecondary: "#5B6B7F",// site --slate
  accent: "#16314A",       // site --navy (was #1E3A8A — the drift this merge kills)
  accentHover: "#0F2438",  // site --navy-ink
  accentSoft: "#EFF4FF",   // kept: no site counterpart (soft accent tint)
  verified: "#1E7A3D",     // site --green
  verifiedBg: "#F0FDF4",   // kept: no site counterpart
  caution: "#9A3412",      // kept: no site counterpart
  cautionBg: "#FFF7ED",    // kept: no site counterpart
  amber: "#92660A",        // site --amber
  amberBg: "#FEF7E6",      // site --amberbg
  neutralChip: "#F1F5F9",  // kept: no site counterpart

  /* THE TINT BORDERS  (2026-08-30, from the approved prototypes).
     A full-strength semantic colour ringing a pale tint — `1px solid #1E7A3D` on #F0FDF4 — is the
     Bootstrap-2 alert, and a design review named it the most dated pattern in the app: 23 call
     sites, several of them full-width bands carrying one sentence. Neither prototype does it. Both
     use a TINT of the same hue for the edge (`.note.ok{border:1px solid #CDE9D6}`), so the box
     reads as a region rather than as an alarm — which is also what this product's own rule asks
     for: "keep disclaimers calm, small, sentence-case — never alarm styling."
     Values are the prototypes' own, not derived here. */
  verifiedLine: "#CDE9D6",
  cautionLine: "#F3DECE",
  amberLine: "#F2E3BE",

  /* THE THIRD INK, RATIFIED. Both prototypes run text / muted / faint, and this file shipped only
     two — so `app.css` hardcoded #8E9AA9 in eight places, `crm.css` fell back to a DIFFERENT faint
     (#8a94a0), and every inline style that wanted a faint reached for `textSecondary` instead,
     which is why labels carried the same weight as the sentences they annotate. */
  textFaint: "#8E9AA9",
} as const;

/* --------------------------------------------------------------------------- *
 * THE SAME PALETTE, AS CASCADE REFERENCES  (2026-09-01, W2-UIFIX)               *
 * --------------------------------------------------------------------------- *
 *
 * WHAT WAS WRONG, measured from `docs/runs/evidence/w1-harness/step3-comps-dark@1920.png`.
 * The dark scheme that landed at 05:45 is written entirely in CSS custom properties, so it can
 * only reach a colour that is READ THROUGH THE CASCADE. Every one of this client's ~640 inline
 * `style={{ color: colors.textSecondary }}` declarations resolves at MODULE LOAD to a literal
 * light-mode hex and can never be anything else. So on `/cma/job_review` in dark, the sale date,
 * the address, the neighbourhood, the distance and the days-on-market column rendered #5B6B7F on
 * a #16273A row — invisible — while the columns whose styling happens to live in `app.css` were
 * correct. Same screen, same data, two different answers about what colour text is.
 *
 * `colors` CANNOT SIMPLY BECOME `var(...)`. It is the input to `cssBridge`, which writes those
 * very properties; a `var()` that resolves to itself is a cycle, and the property falls back to
 * `unset`. `designTokenBridge.test.ts` also pins two of its values as literal hex, correctly —
 * the merged navy is a brand fact and should be asserted as one.
 *
 * So this is a SECOND VIEW of one palette, not a second palette. Every entry is the cascade name
 * that `app.css` already defines at exactly the `colors` value beside it on bare `:root`, and
 * redefines in the two dark blocks. Nothing new is decided here and no hex is invented; the
 * fallback in each `var()` is the literal from `colors` above, so a surface rendered outside the
 * app's stylesheet (a test's jsdom, an email, a print sheet) still gets the ratified colour.
 *
 * HOW A COMPONENT OPTS IN: `import { themeVar as colors } from "../design-tokens"`. One line per
 * file, no call-site edits, and the 453 inline declarations in `cma/` start answering to the
 * cascade. That is deliberate — the alternative was 453 hand edits to change nothing about the
 * light rendering, which is how a mechanical change becomes a design change nobody reviewed.
 *
 * WHERE IT DOES NOT APPLY: anything handed to a canvas, an SVG `fill` computed in JS, or a value
 * this app does arithmetic on. `var()` is a string the browser resolves; JavaScript cannot read
 * a colour out of it. Nothing in `cma/`, `screens/` or `ui/` does either of those with `colors`
 * (checked: no template concatenation, no `.slice`, no alpha suffixing), which is why the swap is
 * safe here and is NOT applied blindly across the client.
 */
export const themeVar: Readonly<Record<keyof typeof colors, string>> = {
  bgApp: `var(--vb-color-bg-app, ${colors.bgApp})`,
  bgSurface: `var(--vb-color-surface, ${colors.bgSurface})`,
  border: `var(--vb-color-border, ${colors.border})`,
  textPrimary: `var(--vb-color-text, ${colors.textPrimary})`,
  textSecondary: `var(--vb-color-text-muted, ${colors.textSecondary})`,
  accent: `var(--vb-color-accent, ${colors.accent})`,
  accentHover: `var(--vb-color-accent-hover, ${colors.accentHover})`,
  accentSoft: `var(--vb-color-accent-soft, ${colors.accentSoft})`,
  verified: `var(--vb-color-success-strong, ${colors.verified})`,
  verifiedBg: `var(--vb-color-success-soft, ${colors.verifiedBg})`,
  caution: `var(--vb-color-danger, ${colors.caution})`,
  cautionBg: `var(--vb-color-danger-soft, ${colors.cautionBg})`,
  amber: `var(--vb-color-warning-strong, ${colors.amber})`,
  amberBg: `var(--vb-color-warning-soft, ${colors.amberBg})`,
  neutralChip: `var(--vb-color-neutral-soft, ${colors.neutralChip})`,
  verifiedLine: `var(--vb-color-success-line, ${colors.verifiedLine})`,
  cautionLine: `var(--vb-color-danger-line, ${colors.cautionLine})`,
  amberLine: `var(--vb-color-warning-line, ${colors.amberLine})`,
  textFaint: `var(--vb-color-text-faint, ${colors.textFaint})`,
} as const;

import type { Tier } from "./types/recommendation";

export interface TierStyle {
  fg: string;
  bg: string;
  /** Text label shown ALONGSIDE the color — color never carries meaning alone (doc rule). */
  label: string;
  /** Icon glyph shown alongside color+text — check/triangle/dash/ⓘ, never color alone. */
  icon: "check" | "triangle" | "dash" | "info";
}

export const tierStyles: Record<Tier, TierStyle> = {
  verified: { fg: colors.verified, bg: colors.verifiedBg, label: "Sourced", icon: "check" },
  indicative: { fg: colors.amber, bg: colors.amberBg, label: "Indicative", icon: "info" },
  assumption: { fg: colors.caution, bg: colors.cautionBg, label: "Assumption", icon: "triangle" },
  entered: { fg: colors.accent, bg: colors.neutralChip, label: "You entered", icon: "info" },
  gap: { fg: colors.textSecondary, bg: colors.bgApp, label: "Not found", icon: "dash" },
};

/**
 * THE SAME FIVE TIERS, THROUGH THE CASCADE  (2026-09-01, W3-UIFIX2).
 *
 * WHY A SECOND OBJECT AND NOT A CHANGE TO THE ONE ABOVE. `designTokenBridge.test.ts` pins
 * `tierStyles.verified.fg` and `.bg` against `cssBridge["--vb-color-success-strong"]` and
 * `"--vb-color-success-soft"`, and it is right to: a VERIFIED fact in the CRM and a "Sourced" chip
 * on a brief are one claim about one thing, and the test is what stops them becoming two greens.
 * `cssBridge` writes those custom properties, so a `tierStyles` whose values were `var(--vb-…)`
 * would make the bridge define a property as itself — a cycle, which resolves to `unset`. The pin
 * and the cascade cannot both live on one object.
 *
 * So this is a SECOND VIEW of the same five decisions, exactly as `themeVar` is of `colors`. No
 * colour is chosen here: every value is `themeVar`'s, the labels and icons are `tierStyles`' own,
 * and the light rendering is unchanged because each `var()` falls back to the literal the frozen
 * object already carries. `designTokenBridge.test.ts` gains one assertion that the two views agree
 * — same five tiers, same labels, same icons, and each `fg`/`bg` a `var()` around the literal the
 * frozen object holds — so they cannot drift apart without a test saying so.
 *
 * WHAT IT IS FOR. `components/SourceDrawer.tsx` and `components/ProvenanceChip.tsx` are the
 * Show-Your-Work surface — the one place the product's central claim is made — and they were the
 * last two files still resolving a tier colour to a frozen light-mode hex at module load. A drawer
 * that opens white on a dark page is the citation UI disagreeing with the app about what colour a
 * page is.
 */
export const themeTierStyles: Record<Tier, TierStyle> = {
  verified: { fg: themeVar.verified, bg: themeVar.verifiedBg, label: "Sourced", icon: "check" },
  indicative: { fg: themeVar.amber, bg: themeVar.amberBg, label: "Indicative", icon: "info" },
  assumption: { fg: themeVar.caution, bg: themeVar.cautionBg, label: "Assumption", icon: "triangle" },
  entered: { fg: themeVar.accent, bg: themeVar.neutralChip, label: "You entered", icon: "info" },
  gap: { fg: themeVar.textSecondary, bg: themeVar.bgApp, label: "Not found", icon: "dash" },
};

/* card 10, not 8 — both prototypes use `--rCard:10px`. control and chip already matched. */
export const radius = { card: "10px", control: "6px", chip: "4px" } as const;

export const shadow = { subtle: "0 1px 2px rgba(15,23,42,.06)" } as const;

// THE ASSET IS CROPPED NOW, SO THESE ARE INK HEIGHTS (2026-08-30).
//
// The note that used to live here said the SVG's viewBox carried ~50% empty vertical padding, so
// ink rendered at half of any CSS height — and that every value below was therefore doubled to
// compensate. It also named the fix and called it the cleaner one: crop the viewBox to its content,
// `229 327 2715 671`, and halve the numbers back.
//
// That crop is now done (`src/assets/valubrief-logo.svg`), so the 2x rule is retired and these are
// what they say: real rendered ink.
//
// WHY THIS IS ALSO THE ANSWER TO "MAKE IT BIGGER". On 2026-08-28 the owner asked for a banner "at
// least 50% bigger"; on 2026-08-30, "double the size of the logo and banner at the top". Taken
// literally against the uncropped asset that produced `appBarWide: 180` — a 425x180px element to
// show ~90px of ink, inside a 188px bar that ate 19% of a 1512x982 viewport before a word of
// content. Two independent design reviews put it first on the list of what makes the product read
// as dated, and it is the reason the app looks nothing like the approved prototype, whose mark is
// 26px in a 54px bar.
//
// Cropped, 40px of CSS height is 40px of INK — against ~30px of ink at the old signed-off 60, and
// ~45px at the 90 that shipped. So the mark on a desktop is genuinely larger than anything the
// owner has approved to date, in a 60px bar instead of a 188px one. Both instructions are honoured;
// the arithmetic that made them destructive is gone.
export const logoHeight = {
  /* Below 520px. Was a bare `44` written inline in AppShell rather than a token — and it was a
     doubled value like the rest, so it rendered ~22px of ink. Named here so the compact tier
     cannot drift away from the others again. */
  /* 2026-08-30, second pass: matched to the approved prototype, which is 26px in a 54px bar in
     BOTH files. Measured against the live site at 1512 the mark rendered 162 × 40 where the
     prototype's renders 61 × 26 — 2.6× the height and 2.7× the width, and the first thing on
     every screen. Note the two marks are not the same lockup: ours is a wide wordmark (aspect
     ~4.05) and the prototype's is a compact one (~2.35), so 26px here renders ~105px wide rather
     than the prototype's 61. That is a decision about the ASSET, not about this number.
     THIS REVERSES A STANDING OWNER INSTRUCTION (2026-08-28, "at least 50% bigger for the desktop
     site") in favour of a later one (2026-08-30, "needs to look this good or better", of the
     prototype). Flagged in the handover rather than changed quietly; one line to put back. */
  appBarCompact: 26,
  appBar: 26,
  appBarWide: 26,
  marketingNav: 34,
  auth: 46,
  footer: 26,
} as const;

export const type = {
  fontUi: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono: "'JetBrains Mono', ui-monospace, monospace", // tabular numerics — non-negotiable per doc
  /* RE-SCALED TO THE APPROVED PROTOTYPE (2026-08-30). The old ramp put `base` at the browser
     default 16px and `md` — which the CSS bridge hands the CRM as its BODY size — at 20px, so the
     CRM's section titles arrived three points over the prototype's 15px. Measured against both
     prototype files: body 14, section heading 17, page heading 22-23, with the 26 reserved for a
     marketing hero. Nothing else in this object changed. */
  /* THE DENSE STEP, RATIFIED 2026-09-01 (W1-THEME). Measured, not chosen: the client carries 471
     inline `fontSize` declarations across 14 distinct values, and the single most-used one is
     13px, at 205 occurrences — more than 14px (63), 12px (91) and 11px (32) put together. It was
     not in the ramp at all, so 205 call sites were, in effect, voting against the scale.

     Two options were on the table and this is the one taken: ratify 13px as a step and say the
     ramp was wrong, rather than book 205 call-site edits to defend a number nobody used. The
     alternative was measured too — moving those 205 sites to `base` (14px) is not a search and
     replace, because roughly a third of them are table cells and chips sized DOWN from body on
     purpose, so each would need a judgement, and the visible result would be a denser product
     rendering one point larger everywhere. That is a design change wearing a cleanup's clothes.

     `dense` sits between `sm` (12.5) and `base` (14) and is deliberately NOT in `cssBridge`: the
     CRM's five sizes are mapped by role and adding a sixth would change that mapping, which
     `designTokenBridge.test.ts` pins. It is available to inline styles and, as
     `--vb-text-dense`, to CSS. */
  scale: { xl: "26px", lg: "22px", md: "17px", base: "14px", dense: "13px", sm: "12.5px", xs: "11px" },
} as const;

/**
 * SPACE — RATIFIED 2026-09-01 (W1-THEME). `--vb-space-1..6` sat deliberately unratified here for
 * months, which is a large part of why 885 inline `padding`/`margin`/`gap` declarations exist:
 * there was no scale to reach for.
 *
 * THE SCALE IS MEASURED, NOT IDEALISED. Parsing those 885 declarations gives 1241 numeric
 * components. Their distribution:
 *
 *     0:305   2:40   3:20   4:44   5:12   6:105   7:10   8:168   9:27  10:117  11:9
 *    12:111  14:67  16:63  18:39  20:42  22:10  24:23  26:3  28:5  30:2  32:5  48:4
 *
 * The product's real density grid is 2px, not 4px: 6 (105), 10 (117), 14 (67) and 18 (39)
 * together outnumber 16 (63) three to one. A tidy 4/8/16/24/32 absorbs 303 of 936 non-zero
 * components — 32%. The ladder below absorbs 91% exactly, and 95% within one pixel. A scale that
 * two thirds of the codebase has to ignore is not a scale; it is a second opinion.
 *
 * NAMED BY VALUE, not by index, so `space[8]` is unambiguously 8px and a step can be inserted
 * without renumbering. The old index names are retired rather than aliased — see the note in
 * `app.css`'s `:root` block and the six re-pointed lines in `crm/crm.css`.
 */
export const space = {
  0: "0",
  2: "2px",
  4: "4px",
  6: "6px",
  8: "8px",
  10: "10px",
  12: "12px",
  14: "14px",
  16: "16px",
  18: "18px",
  20: "20px",
  24: "24px",
  32: "32px",
  48: "48px",
} as const;

/* THROUGH THE CASCADE, for the reason `themeVar` gives above: a focus ring baked to the light
   navy is invisible on a #0F1720 page, and a focus ring is the one affordance that may never be
   conditional on a colour scheme. Same colour on light, resolved rather than frozen. */
export const focusRing = `2px solid ${themeVar.accent}`; // 2px, offset 2px, per WCAG 2.2 AA note in the doc

/* --------------------------------------------------------------------------- *
 * The CSS bridge — why the CRM looked like a different product                  *
 * --------------------------------------------------------------------------- *
 *
 * `crm/crm.css` consumes `var(--vb-<name>, <fallback>)` throughout, and says in its own header
 * that those names are "a proposal, not a claim" written without being able to see this app's
 * CSS, with fallbacks that are "NOT a second design system … they exist so this module renders
 * sanely before anyone has wired it up".
 *
 * Nobody ever wired it up. Measured 2026-08-28: nothing in this client defined a single `--vb-*`
 * property, so all sixty fallbacks were live and the CRM shipped its placeholder palette —
 * `system-ui` instead of Inter (the same defect class as UI-130) on a 12/13/15/19/26 scale
 * against the ratified 12/14/16/20/24, in neutral greys instead of Calm Fintech. Wrong typeface,
 * wrong scale, wrong colour. That is what "archaic, and doesn't match the flow" was.
 *
 * WHY THIS IS TYPESCRIPT AND NOT A `:root{}` BLOCK IN `app.css`. CSS cannot import this module,
 * so a stylesheet version means copying seventeen hex values out of here by hand — and the first
 * draft of exactly that copy already got one wrong (`rgba(…,0.06)` for `rgba(…,.06)`). Built here
 * instead, there is no copy, so there is nothing to drift: this file remains the ONE source that
 * ADR-0003 requires, and the CSS reads it.
 *
 * DELIBERATELY ABSENT: `--vb-color-border-strong`, `--vb-color-text-faint` and `--vb-space-1..6`.
 * No token ratifies them, `crm.css` already carries a working fallback for each, and a colour is
 * a brand decision — this file exists precisely so nobody derives one in passing. The
 * design-system owner should ratify them or confirm the fallbacks. `designTokenBridge.test.ts`
 * asserts they stay unset so nobody quietly fills them in with a plausible hex.
 */
export const cssBridge: Readonly<Record<string, string>> = {
  // Type. The CRM names five sizes; `type.scale` ratifies six, so the mapping is BY ROLE rather
  // than by matching names: xs meta, sm table body, md body, lg section heading, xl page heading.
  // `scale.xl` (30px) is the marketing hero size and stays out of the CRM on purpose.
  "--vb-font-sans": type.fontUi,
  "--vb-font-mono": type.fontMono,
  "--vb-text-xs": type.scale.xs,
  "--vb-text-sm": type.scale.sm,
  "--vb-text-md": type.scale.base,
  "--vb-text-lg": type.scale.md,
  "--vb-text-xl": type.scale.lg,

  // Surfaces and ink
  "--vb-color-surface": colors.bgSurface,
  "--vb-color-surface-raised": colors.bgSurface,
  "--vb-color-surface-sunken": colors.bgApp,
  "--vb-color-border": colors.border,
  "--vb-color-text": colors.textPrimary,
  "--vb-color-text-muted": colors.textSecondary,
  /* RATIFIED 2026-08-30 — see `colors.textFaint`. It was deliberately absent while unratified, and
     `designTokenBridge.test.ts` asserted that; the assertion moves with the decision. */
  "--vb-color-text-faint": colors.textFaint,

  // Accent
  "--vb-color-accent": colors.accent,
  "--vb-color-accent-contrast": colors.bgSurface,
  "--vb-color-accent-soft": colors.accentSoft,

  // The evidence bands. These are the SAME colours the provenance chips use elsewhere, which is
  // the point: a VERIFIED fact in the CRM and a "Sourced" chip on a brief are one claim about one
  // thing, and must not be two different greens.
  "--vb-color-success-strong": colors.verified,
  "--vb-color-success-soft": colors.verifiedBg,
  "--vb-color-warning-strong": colors.amber,
  "--vb-color-warning-soft": colors.amberBg,
  "--vb-color-neutral-strong": colors.textSecondary,
  "--vb-color-neutral-soft": colors.neutralChip,
  "--vb-color-danger": colors.caution,
  "--vb-color-danger-soft": colors.cautionBg,

  // Geometry
  "--vb-radius-sm": radius.chip,
  "--vb-radius-md": radius.card,
  "--vb-radius-pill": "999px", // geometry, not a brand decision — no token needed
  "--vb-shadow-sm": shadow.subtle,
} as const;

/** Apply the bridge to a document. Called once, from `main.tsx`, before the app renders. */
export function applyCssBridge(root: HTMLElement): void {
  for (const [name, value] of Object.entries(cssBridge)) root.style.setProperty(name, value);
}

/* --------------------------------------------------------------------------- *
 * THEME CHOICE  (2026-09-01, W1-THEME)                                          *
 * --------------------------------------------------------------------------- *
 *
 * WHY `applyCssBridge` IS NO LONGER CALLED ON `<html>`. It writes the `--vb-*` names as INLINE
 * custom properties, and an inline custom property beats every author rule — including a
 * `@media (prefers-color-scheme: dark)` block and a `[data-theme]` block. While the bridge was
 * applied inline, a dark scheme was not merely unwritten, it was unexpressible. That is the real
 * reason this client had none; `crm.css:1010` still carries the comment explaining why the dark
 * block was deleted rather than fixed.
 *
 * So the CSS side of the bridge now lives in `app.css`'s `:root` block, inside the cascade where
 * a media query can reach it. `cssBridge` and `applyCssBridge` are UNCHANGED and still exported —
 * they remain the TS-side record and `designTokenBridge.test.ts` still pins them — and a parity
 * check asserts every name in `cssBridge` is defined on bare `:root` at the same value
 * (see docs/runs/evidence/w1-theme/REPORT.md).
 *
 * THREE STATES, NOT TWO. An explicit choice stamps `data-theme`, which wins in both directions;
 * "system" stamps NOTHING, so `prefers-color-scheme` decides.
 *
 * LIGHT IS THE DEFAULT (2026-09-01, owner directive), NOT "system".
 *
 * The previous default was "system", on the argument that forcing light on a person whose machine
 * is dark overrides a preference the product was never asked to have an opinion about. That
 * argument is sound and it is not what shipped: with "system" as the default, the FIRST time most
 * of this product was ever seen in dark was on an owner's screen, unreviewed, and it was carrying
 * two screens with no dark palette at all. A default is a promise about which palette has actually
 * been checked, and light is the one that has.
 *
 * The cost is stated rather than hidden: a person whose whole machine is dark now gets a light app
 * until they change it, which is exactly the override the old default avoided. The mitigation is
 * that the control sits in the app bar on every screen and "System" is still one of its three
 * options — the preference is one click away rather than assumed.
 *
 * "SYSTEM" IS NOW STORED RATHER THAN REPRESENTED BY ABSENCE, and it has to be. It used to be
 * written by REMOVING the key, which worked only while an absent key also meant "system". With
 * light as the default that spelling would make "System" unselectable: choose it, reload, read
 * nothing, land on light. So the key now holds all three values and absence means "never chose".
 *
 * Storage is per-browser and best-effort: a private window, cleared site data or a browser set to
 * block storage all throw or return null, so every access is wrapped and the absence of a stored
 * value is a valid answer, not an error.
 */
export type ThemeChoice = "light" | "dark" | "system";

const THEME_KEY = "vb.theme";

/** What a browser with nothing stored gets. See this section's note for why it is not "system". */
export const DEFAULT_THEME: ThemeChoice = "light";

/** What has been chosen, or `DEFAULT_THEME` when nothing has. */
export function readThemeChoice(): ThemeChoice {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* storage unavailable — the default is the correct answer, not a failure */
  }
  return DEFAULT_THEME;
}

/**
 * Stamp the choice on the root element. `system` REMOVES the attribute rather than setting a
 * value, so the media query is what decides — an attribute of `data-theme="system"` would match
 * neither guard and silently pin the page to the light palette on bare `:root`.
 */
export function applyThemeChoice(choice: ThemeChoice, root?: HTMLElement): void {
  const el = root ?? document.documentElement;
  if (choice === "system") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", choice);
}

/** Persist and apply. Returns the choice so a caller can render it without re-reading storage. */
export function setThemeChoice(choice: ThemeChoice, root?: HTMLElement): ThemeChoice {
  try {
    // ALL THREE VALUES ARE WRITTEN, "system" included. Removing the key for "system" was correct
    // while absence meant "system"; now absence means "never chose" and the default is light, so
    // removing it would silently discard the choice on the next reload.
    window.localStorage.setItem(THEME_KEY, choice);
  } catch {
    /* the choice still applies for this page view; it just will not survive a reload */
  }
  applyThemeChoice(choice, root);
  return choice;
}
